// Pause overlay: resume, settings, build summary, controls, abandon run.

import Phaser from 'phaser';
import { SCENES } from '../config/constants';
import { COLORS } from '../config/palette';
import { clearRun, getRun } from '../core/run';
import { session } from '../core/session';
import { describeItem } from '../systems/loot/describe';
import { synergyColor, synergyName } from '../ui/format';
import { MenuNav, Panel, TYPE, UI, backdrop, confirmDialog, createButton, createChip, footerHints, setMenuCursor, sfx, txt } from '../ui/kit';

const CONTROLS: [string, string][] = [
  ['A / D', 'Move'],
  ['Space', 'Jump / double jump'],
  ['S + Space', 'Drop through platform'],
  ['Shift', 'Dash'],
  ['Mouse', 'Aim'],
  ['Left mouse', 'Fire'],
  ['Right mouse', 'Signature power'],
  ['Q', 'Ultimate'],
  ['E / X', 'Equip / salvage'],
  ['1 · 2 · Wheel', 'Switch weapon'],
  ['Tab', 'Sector map'],
];

export class PauseScene extends Phaser.Scene {
  private nav!: MenuNav;

  constructor() {
    super(SCENES.PAUSE);
  }

  create(): void {
    setMenuCursor(this, true);
    backdrop(this, 0.8);
    const run = getRun();
    const accent = run?.mech.reactorColor ?? COLORS.volt;
    const pw = 1500;
    const ph = 800;
    const px = (UI.W - pw) / 2;
    const py = (UI.H - ph) / 2;
    new Panel(this, px, py, pw, ph, { fill: COLORS.uiBg, fillAlpha: 0.95, accent, ticks: true, chamfer: 22, header: 96, headerAlpha: 0.1, scanlines: true });
    txt(this, px + 52, py + 22, 'PAUSED', { font: 'display', size: TYPE.h1, weight: 700, spacing: 8 });

    this.nav = new MenuNav(this, { layout: 'vertical', onBack: () => this.resume(), keys: { Escape: () => this.resume() } });
    const bx = px + 210;
    let by = py + 170;
    const resume = createButton(this, bx, by, { label: 'Resume', width: 320, height: 62, variant: 'primary', accent, onClick: () => this.resume() }, this.nav);
    by += 80;
    createButton(this, bx, by, { label: 'Settings', width: 320, height: 62, onClick: () => this.openSettings() }, this.nav);
    by += 80;
    createButton(this, bx, by, { label: 'Abandon run', width: 320, height: 62, variant: 'danger', onClick: () => this.abandon() }, this.nav);

    // Build summary.
    const cx = px + 440;
    txt(this, cx, py + 130, 'BUILD', { font: 'display', size: TYPE.label, color: accent, spacing: 4, weight: 700 });
    let y = py + 170;
    if (run) {
      const inv = run.inventory;
      const items = inv.equippedItems();
      for (const item of items.slice(0, 12)) {
        let name: string = item.defId;
        let kind = '';
        let color: number = COLORS.uiText;
        try {
          const d = describeItem(item);
          name = d.name;
          kind = d.kindLabel;
          color = d.color;
        } catch {
          /* fallback ids */
        }
        txt(this, cx, y, name, { font: 'display', size: TYPE.small, color, weight: 600 });
        txt(this, cx + 330, y + 2, kind, { size: TYPE.micro, color: COLORS.uiDim });
        y += 34;
      }
      const syn = session.ctx?.effects.activeSynergies ?? inv.activeSynergies(run.mech.kitTags);
      let chipX = cx;
      for (const s of syn) {
        const chip = createChip(this, chipX, y + 20, synergyName(s), synergyColor(s));
        chipX += chip.getBounds().width + 10;
      }
      y += 60;
      txt(this, cx, y, `SCRAP ${run.scrap}    ROOMS ${run.stats.roomsCleared}    KILLS ${run.stats.kills}`, { font: 'display', size: TYPE.label, color: COLORS.uiDim, spacing: 2 });
    }

    // Controls.
    const kx = px + pw - 470;
    txt(this, kx, py + 130, 'CONTROLS', { font: 'display', size: TYPE.label, color: accent, spacing: 4, weight: 700 });
    CONTROLS.forEach(([k, v], i) => {
      txt(this, kx, py + 172 + i * 40, k, { font: 'display', size: TYPE.small, color: COLORS.uiText, weight: 600 });
      txt(this, kx + 190, py + 172 + i * 40, v, { size: TYPE.small, color: COLORS.uiDim });
    });

    footerHints(this, [
      { action: 'confirm', label: 'Select' },
      { action: 'back', label: 'Resume' },
    ]);
    this.nav.focus(resume, { silent: true });
    this.nav.lock(200);
  }

  update(): void {
    this.nav?.update();
  }

  private resume(): void {
    sfx('ui_back');
    this.scene.resume(SCENES.GAME);
    this.scene.stop();
  }

  private openSettings(): void {
    this.scene.launch(SCENES.SETTINGS, { returnTo: SCENES.PAUSE });
    this.scene.pause();
  }

  private abandon(): void {
    confirmDialog(this, {
      title: 'Abandon this run?',
      body: 'Your current build is lost. Cores are only earned by finishing or dying in a run.',
      confirmLabel: 'Abandon',
      cancelLabel: 'Keep fighting',
      danger: true,
      parentNav: this.nav,
      onConfirm: () => {
        this.scene.stop(SCENES.GAME);
        this.scene.stop(SCENES.UI);
        clearRun();
        this.scene.start(SCENES.MENU);
      },
    });
  }
}
